import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Map, Source, Layer, Marker, FullscreenControl, NavigationControl } from "react-map-gl/maplibre";
import type { MapLayerMouseEvent } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { MAX_ZOOM, fitView, positionsOf, readShape } from "../../geo/areaShapes";
import type { AreaShape, ShapeInput } from "../../geo/areaShapes";
import { theme, basemapStyle } from "../../config/theme";
import MapAttribution from "./MapAttribution";
import { useTheme } from "../../contexts/ThemeContext";
import { useIsPhone } from "../../hooks/useIsPhone";

/**
 * Whether the map is laid out for a phone: its overlays then share one sheet
 * along the bottom edge instead of a column down the left. Overlays read it to
 * start compact (the totals collapse to their headline) and drop editing.
 */
const MapPhoneContext = createContext(false);
export const useMapPhone = () => useContext(MapPhoneContext);

export interface AreaPolygon {
  /** Stable id, reported back through `onSelect`. Defaults to the array index. */
  id?: string;
  name?: string;
  /** The area's shape: WKT text, the `{ value }` wrapper or GeoJSON. */
  wkt: ShapeInput;
  color?: string;
  /** Optional pill rendered at the polygon's centre (e.g. a live count). */
  label?: React.ReactNode;
}

const DEFAULT_COLOR = theme.brand500;

/** Centre of a shape's bounding box, for anchoring a label. */
function centreOf(shape: AreaShape): { longitude: number; latitude: number } | null {
  const points = positionsOf([shape]);
  if (points.length === 0) return null;
  const lngs = points.map((p) => p[0]);
  const lats = points.map((p) => p[1]);
  return {
    longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
  };
}

/**
 * Polygons on a basemap, built to be explored rather than glanced at: native
 * fullscreen and zoom controls, click-to-select (fills and label pills), and an
 * optional `panel` that renders INSIDE the map container so it stays visible in
 * fullscreen — the caller decides what goes in it. Purely presentational.
 */
const RealtimeAreaMap: React.FC<{
  polygons: AreaPolygon[];
  fillOpacity?: number;
  /** Id of the highlighted polygon, if any. */
  selectedId?: string | null;
  /** Called with a polygon id, or null when the empty map is clicked. */
  onSelect?: (id: string | null) => void;
  /** Top-left column, above `panel` — e.g. running totals. Brings its own card styling. */
  overlayTopLeft?: React.ReactNode;
  /** Top-left column, below `overlayTopLeft` — e.g. the selection's details. Brings its own card styling. */
  panel?: React.ReactNode;
  /** Top-right, beside the map controls — e.g. a "last updated" stamp. Not shown on a phone. */
  overlayTopRight?: React.ReactNode;
  /** When set, these polygons are emphasised and the rest dimmed (e.g. members of the total being edited). */
  highlightIds?: Set<string> | null;
  /** Bottom-left, e.g. a display-mode switch or legend. Not shown on a phone. */
  overlayBottomLeft?: React.ReactNode;
  /** Keep the WebGL drawing buffer so the map survives printing / PDF export. */
  printable?: boolean;
  /** Closest the initial view may zoom when framing the polygons (e.g. a report close-up). */
  maxZoom?: number;
  /** Map layers that draw beneath the area outlines (e.g. a choropleth). Layers must target `fill-0` with `beforeId`. */
  underlay?: React.ReactNode;
  }> = ({ polygons, fillOpacity = 0.15, selectedId = null, onSelect, overlayTopLeft, panel, overlayTopRight, highlightIds = null, overlayBottomLeft, printable = false, maxZoom = MAX_ZOOM, underlay }) => {
  const [hovering, setHovering] = useState(false);
  const { isDark } = useTheme();
  // A printed map keeps the desktop layout whatever screen it was opened on.
  const phone = useIsPhone() && !printable;
  const parsed = useMemo(
    () =>
      polygons.flatMap((p, i) => {
        const geometry = readShape(p.wkt);
        return geometry
          ? [{ id: p.id ?? String(i), name: p.name, color: p.color || DEFAULT_COLOR, label: p.label, geometry }]
          : [];
      }),
    [polygons]
  );
  const fillIds = useMemo(() => parsed.map((_, i) => `fill-${i}`), [parsed]);
  const handleClick = (e: MapLayerMouseEvent) => {
    if (!onSelect) return;
    const hit = e.features?.[0]?.properties?.id;
    onSelect(typeof hit === "string" ? hit : null);
  };
  // The map opens on the polygons, fitted to the size it really has (measured once, before the first paint).
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (el) setBox({ width: el.clientWidth, height: el.clientHeight });
  }, [parsed.length]);
  const view = useMemo(
    () =>
      parsed.length && box
        ? fitView(parsed.map((p) => p.geometry), null, {
            width: box.width || undefined,
            height: box.height || undefined,
            maxZoom,
          })
        : null,
    [parsed, box, maxZoom]
  );

  if (!parsed.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-faint">
        Location unavailable
      </div>
    );
  }

  if (!view) return <div ref={boxRef} style={{ width: "100%", height: "100%" }} />;

  return (
    <div ref={boxRef} style={{ width: "100%", height: "100%" }}>
      <Map
        initialViewState={view}
        mapStyle={basemapStyle(isDark)}
        attributionControl={false}
        // Without this the canvas is cleared after each frame and prints blank.
        preserveDrawingBuffer={printable}
        style={{ width: "100%", height: "100%" }}
        interactiveLayerIds={onSelect ? fillIds : undefined}
        onClick={handleClick}
        onMouseMove={onSelect ? (e) => setHovering(!!e.features?.length) : undefined}
        cursor={hovering ? "pointer" : "grab"}
      >
        {/* Native controls: fullscreen takes the whole container (panel and labels
            included) and the +/- pair makes zooming obvious on a large screen. */}
        <FullscreenControl position="top-right" />
        <NavigationControl position="top-right" showCompass={false} />

        {parsed.map((p, i) => {
          const selected = p.id === selectedId;
          // In edit mode the members of the group read strong and everything else recedes.
          const emphasised = highlightIds ? highlightIds.has(p.id) : selected;
          const dimmed = !!highlightIds && !highlightIds.has(p.id);
          const data = { type: "Feature", geometry: p.geometry, properties: { id: p.id } } as any;
          return (
            <Source key={i} id={`area-${i}`} type="geojson" data={data}>
              <Layer
                id={`fill-${i}`}
                type="fill"
                paint={{ "fill-color": p.color, "fill-opacity": emphasised ? Math.min(fillOpacity + 0.2, 0.7) : dimmed ? fillOpacity * 0.35 : fillOpacity } as any}
              />
              <Layer
                id={`line-${i}`}
                type="line"
                paint={{ "line-color": p.color, "line-width": emphasised ? 5 : dimmed ? 1.5 : 2.5 } as any}
              />
            </Source>
          );
        })}

        {underlay}

        {/* Labels sit above the fills so a zone's number is readable without
            hovering — colour alone can't carry a count. */}
        {parsed.map((p, i) => {
          if (!p.label) return null;
          const c = centreOf(p.geometry);
          const selected = p.id === selectedId;
          return c ? (
            <Marker
              key={`label-${i}`}
              longitude={c.longitude}
              latitude={c.latitude}
              onClick={
                onSelect
                  ? (e) => {
                      e.originalEvent.stopPropagation();
                      onSelect(highlightIds ? p.id : selected ? null : p.id);
                    }
                  : undefined
              }
            >
              <div
                className={`whitespace-nowrap rounded-lg border bg-surface/95 px-2 py-1 text-xs shadow-soft ${
                  onSelect ? "cursor-pointer hover:border-brand-500" : "pointer-events-none"
                } ${(highlightIds ? highlightIds.has(p.id) : selected) ? "border-brand-500 ring-2 ring-brand-500/30" : "border-paper-300"} ${
                  highlightIds && !highlightIds.has(p.id) ? "opacity-60" : ""
                }`}
                title={p.name}
              >
                {p.label}
              </div>
            </Marker>
          ) : null;
        })}

        {/* Overlays live inside the map container on purpose: they must survive fullscreen. */}
        <MapPhoneContext.Provider value={phone}>
          {phone ? (
            // On a phone a left column would cover most of the map. One sheet
            // along the bottom shows the selected zone, else the totals; the
            // corner overlays are the page's job to show above the map.
            (panel || overlayTopLeft) && (
              <div className="absolute inset-x-2 bottom-6 z-10 flex max-h-[70%] flex-col overflow-auto">
                {panel || overlayTopLeft}
              </div>
            )
          ) : (
            <>
              {(overlayTopLeft || panel) && (
                <div className="absolute left-3 top-3 z-10 flex max-h-[calc(100%-1.5rem)] w-72 flex-col gap-2 overflow-auto">
                  {overlayTopLeft}
                  {panel}
                </div>
              )}
              {overlayTopRight && <div className="absolute right-14 top-3 z-10">{overlayTopRight}</div>}
              {overlayBottomLeft && <div className="absolute bottom-3 left-3 z-10">{overlayBottomLeft}</div>}
            </>
          )}
        </MapPhoneContext.Provider>
        {/* Printed too, so the credit survives PDF export. */}
        <MapAttribution />
      </Map>
    </div>
  );
};

export default RealtimeAreaMap;
